/**
 * Display-name mapping. Internal codes ("trapline", "overclaim",
 * "evidence_commentary") are precise but useless for students. Every
 * user-facing label goes through this map.
 *
 * Authoritative for UX — if a label is missing here, the app falls back
 * to humanize() which lowercases + replaces underscores with spaces.
 */

const MODULES: Record<string, string> = {
  grammar: "Grammar",
  trapline: "Reading",
  rubric: "AP Essays",
  prompt: "Application Essays",
  slice: "Math Strategy",
  science: "Data & Figures",
  calc: "AP Calculus",
  apprecalc: "AP Precalculus",
  act_math: "ACT Math",
  act_english: "ACT English",
  act_reading: "ACT Reading",
  act_science: "ACT Science",
};

const MODULE_BLURBS: Record<string, string> = {
  grammar: "Spot the rule. Catch the trap.",
  trapline: "Read closely. Avoid the wrong-answer traps.",
  rubric: "Match what graders are actually looking for.",
  prompt: "Read what the prompt really asks.",
  slice: "Pick the fastest path to the answer.",
  science: "Read the data. Judge the evidence.",
  calc: "Set it up right. Spot the calculus trap.",
  apprecalc: "Polynomials, logs and trig, the way AP asks them.",
  act_math: "Logs, sequences, matrices, and more the ACT tests.",
  act_english: "Read the stem, fix the passage. ACT English format.",
  act_reading: "Find the line that proves it. ACT Reading format.",
  act_science: "Read the graph, run the numbers. ACT Science format.",
};

// Skill family → human-readable description.
// First clause is the short label (used in lists). Second clause (optional)
// is a one-line explainer (used in reveal sheets / mistakes view).
const SKILL_FAMILIES: Record<string, { short: string; long?: string }> = {
  // Reading skills folded into the Reading drill (official SAT Craft & Structure)
  words_in_context: {
    short: "Words in context",
    long: "Pick the word that fits the blank on both meaning and connotation; the wrong choices are near-misses.",
  },
  cross_text_connections: {
    short: "Cross-text connections",
    long: "Judge how the two texts relate; the wrong choices misstate one text's view.",
  },
  // Trapline (SAT Reading-style traps)
  overclaim: {
    short: "Answer too strong",
    long: "The answer uses absolutes (\"always\", \"solve\", \"never\") that the passage doesn't support.",
  },
  wrong_scope: {
    short: "Off topic",
    long: "The answer drifts to something the passage doesn't actually address.",
  },
  reversed_relation: {
    short: "Cause and effect reversed",
    long: "The answer flips the direction of the relationship the passage describes.",
  },
  true_but_irrelevant: {
    short: "True, but doesn't answer",
    long: "The statement is accurate but isn't what the question is asking about.",
  },
  tone_mismatch: {
    short: "Wrong tone",
    long: "The answer mis-reads the author's attitude or register.",
  },
  unsupported_inference: {
    short: "Goes beyond the passage",
    long: "The answer requires information the passage doesn't actually say.",
  },
  opposite_polarity: {
    short: "Opposite of the passage",
    long: "The answer states the literal reverse of what the passage argues.",
  },
  evidence_paraphrase_distortion: {
    short: "Paraphrases incorrectly",
    long: "The answer re-states the passage but shifts the meaning.",
  },
  cherry_pick_detail: {
    short: "Picks one detail",
    long: "The answer fixates on a small detail and misses the main point.",
  },

  // Grammar
  modifier_attachment: { short: "Misplaced modifier" },
  parallel_structure: { short: "Parallel structure" },
  comma_usage: { short: "Comma rules" },
  semicolon_colon_dash: { short: "Semicolons & colons" },
  subject_verb_agreement: { short: "Subject-verb agreement" },
  pronoun_reference: { short: "Pronoun reference" },
  sentence_boundary: { short: "Sentence boundaries" },
  transition_fit: { short: "Transitions" },
  wordy_redundancy: { short: "Wordiness" },
  apostrophe_possession: { short: "Apostrophes" },
  idiomatic_preposition: { short: "Preposition idioms" },
  who_whom: { short: "Who vs. whom" },
  comparison_structure: { short: "Comparisons" },

  // Rubric
  thesis: { short: "Thesis" },
  evidence_commentary: { short: "Evidence & analysis" },
  contextualization: { short: "Historical context" },
  sophistication: { short: "Sophistication" },
  complex_understanding: { short: "Complex understanding" },
  sourcing: { short: "Document sourcing" },
  analysis_reasoning: { short: "Analysis & reasoning" },
  missing_element: { short: "What is missing" },
  earns_no_credit: { short: "Earns no credit" },
  drag_evidence: { short: "Match the evidence" },
  aaq_research_method: { short: "Research method" },
  aaq_operational_definition: { short: "Defining a variable" },
  aaq_statistic_interpretation: { short: "Reading the statistic" },
  aaq_ethical_guideline: { short: "Ethical guidelines" },
  aaq_generalizability: { short: "How far it generalizes" },
  aaq_argumentation: { short: "Arguing from a result" },
  ebq_claim: { short: "Making a claim" },
  ebq_evidence: { short: "Citing evidence" },
  ebq_reasoning: { short: "Linking evidence" },
};

export function moduleLabel(id: string): string {
  return MODULES[id] ?? humanize(id);
}

export function moduleBlurb(id: string): string {
  return MODULE_BLURBS[id] ?? "";
}

export function skillFamilyShort(family: string): string {
  const known = SKILL_FAMILIES[family]?.short;
  if (known) return known;
  /**
   * A VALUE THIS TABLE DOES NOT KNOW IS NEVER SHOWN RAW.
   *
   * humanize() was the fallback, and humanize() will render anything: a public
   * topic id arrived in this column and a student's home screen read
   * "Weakest: Science.science content.biology". Nothing crashed and nothing
   * looked broken from the code's side, which is why it sat in production until
   * somebody read it.
   *
   * A dotted or namespaced value is an identifier that escaped from somewhere,
   * not a skill name. Rendering the last segment is the best honest guess;
   * rendering the whole thing is showing a student our internals.
   */
  if (/[.:/]/.test(family)) {
    const leaf = family.split(/[.:/]/).filter(Boolean).pop() ?? "";
    return leaf ? humanize(leaf) : "";
  }
  return humanize(family);
}

export function skillFamilyLong(family: string): string | undefined {
  return SKILL_FAMILIES[family]?.long;
}

export function humanize(s: string | null | undefined): string {
  // Guard against an undefined key reaching here: humanize is the fallback for
  // moduleLabel() + skillFamilyShort(), which are called in ~25 places with
  // data-derived module / skill_family / signature-label values. A single
  // undefined (a malformed item, an aggregate row with no family) would
  // otherwise crash the whole screen on `.replace` of undefined.
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .replace(/\b(\w)/g, (m) => m.toLowerCase())
    .replace(/^./, (m) => m.toUpperCase());
}
